/** Rueckfrage beim Speichern eines Einsatzes, der ueber Ferien laeuft.
 *
 *  Zeigt beide Arten, die Ferien auszusparen, mit den Teilen, die dabei entstehen:
 *  Verlaengern haelt die Wochenzahl und haengt den Rest hinter die Ferien, Kuerzen
 *  haelt das Ende und laesst die Ferienwochen weg. Liegt immer ueber dem offenen
 *  Einsatz-Formular (Ebene 2), damit Abbrechen zurueck ins Formular fuehrt.
 */

import { openModal2, closeModal2, modalBody2, onModal2Close } from "./modal";
import { Icons } from "../utils/icons";
import { sameParts, type WeekSpan } from "../utils/vacation-split";
import { esc } from "../utils/html";

export type VacationSplitChoice = "keep" | "shorten" | "extend";

export interface VacationSplitOptions {
  /** Der Einsatz, wie er im Formular steht. */
  span: WeekSpan;
  /** Ferien, die in den Einsatz fallen. */
  vacations: { name: string; startWeek: number; endWeek: number }[];
  extend: { parts: WeekSpan[]; missingWeeks: number; overlaps: WeekSpan[] };
  shorten: WeekSpan[];
  /** Beschriftung eines Wochenbereichs, etwa "KW 40 bis KW 42". */
  rangeLabel: (startWeek: number, endWeek: number) => string;
}

const weeksText = (n: number) => `${n} ${n === 1 ? "Woche" : "Wochen"}`;
const total = (parts: WeekSpan[]) => parts.reduce((s, p) => s + p.durationWeeks, 0);

export function vacationSplitDialog(opts: VacationSplitOptions): Promise<VacationSplitChoice | null> {
  return new Promise(resolve => {
    const { span, rangeLabel } = opts;
    const spanLabel = rangeLabel(span.startWeek, span.startWeek + span.durationWeeks - 1);
    const complete = Array.from({ length: span.durationWeeks }, (_, i) => span.startWeek + i)
      .every(w => opts.vacations.some(v => v.startWeek <= w && v.endWeek >= w));

    const partsHtml = (parts: WeekSpan[]) => `<ul class="vsplit-parts">${parts.map(p =>
      `<li><span>${esc(rangeLabel(p.startWeek, p.startWeek + p.durationWeeks - 1))}</span><span>${weeksText(p.durationWeeks)}</span></li>`
    ).join("")}</ul>`;
    const note = (text: string) => `<div class="vsplit-note">${esc(text)}</div>`;
    const sumLine = (parts: WeekSpan[]) => total(parts) < span.durationWeeks
      ? `<div class="vsplit-sum">Zusammen ${total(parts)} statt ${weeksText(span.durationWeeks)}.</div>`
      : "";

    // Beide Varianten sind gleichwertig, deshalb gleich hervorgehobene Knoepfe.
    const card = (choice: VacationSplitChoice, title: string, sub: string, body: string, label: string, disabled: boolean) => `
      <div class="vsplit-option">
        <div class="vsplit-option-title">${esc(title)}</div>
        <div class="vsplit-option-sub">${esc(sub)}</div>
        ${body}
        <button class="btn btn-primary" data-choice="${choice}" ${disabled ? "disabled" : ""}>${esc(label)}</button>
      </div>`;

    const { extend, shorten } = opts;
    const extendNotes = [
      extend.missingWeeks > 0
        ? note(`Nur ${total(extend.parts)} von ${weeksText(span.durationWeeks)} passen noch in den Zeitraum.`) : "",
      ...extend.overlaps.map(o =>
        note(`Überschneidet sich in ${rangeLabel(o.startWeek, o.startWeek + o.durationWeeks - 1)} mit einem anderen Einsatz.`)),
    ].join("");

    const single = sameParts(extend.parts, shorten);
    let cards: string;
    if (single) {
      // Der Zeitraum endet, bevor verlaengert werden kann: beide Arten ergeben dasselbe,
      // und neue Wochen kommen nicht hinzu, also auch keine neuen Ueberschneidungen.
      cards = card("shorten", "Ferien aussparen", "Für eine Verlängerung reicht der Zeitraum nicht.",
        shorten.length ? partsHtml(shorten) + sumLine(shorten)
          : note("Nicht möglich, im Zeitraum bleibt keine Woche außerhalb der Ferien."),
        "Ferien aussparen", shorten.length === 0);
    } else {
      cards = card("extend", "Verlängern", "Die Wochenzahl bleibt, der Rest folgt nach den Ferien.",
        extend.parts.length ? partsHtml(extend.parts) + extendNotes
          : note("Nicht möglich, nach den Ferien bleibt im Zeitraum keine Woche mehr."),
        "Aussparen und verlängern", extend.parts.length === 0)
        + card("shorten", "Kürzen", "Das Ende bleibt, die Ferienwochen entfallen.",
          shorten.length ? partsHtml(shorten) + sumLine(shorten)
            : note("Nicht möglich, der Einsatz liegt komplett in den Ferien."),
          "Aussparen und kürzen", shorten.length === 0);
    }

    const vacList = opts.vacations.map(v =>
      `<li>${Icons.sun}<span>${esc(v.name)}: ${esc(rangeLabel(v.startWeek, v.endWeek))}</span></li>`).join("");

    openModal2("Einsatz überschneidet sich mit Ferien", `
      <p class="vsplit-intro">Der Einsatz (${weeksText(span.durationWeeks)}, ${esc(spanLabel)}) liegt ${complete ? "komplett" : "teilweise"} in den Ferien:</p>
      <ul class="vsplit-vacations">${vacList}</ul>
      <p class="vsplit-intro">Sollen die Ferien ausgespart werden? Der Einsatz wird dann ohne die Ferienwochen eingetragen.</p>
      <div class="vsplit-options">${cards}</div>
      <div class="form-actions">
        <button class="btn btn-secondary" id="vs-cancel">Abbrechen</button>
        <button class="btn btn-secondary" data-choice="keep">Nicht aussparen</button>
      </div>`, { wide: !single });

    let decided = false;
    const decide = (value: VacationSplitChoice | null) => {
      if (decided) return;
      decided = true;
      resolve(value);
    };
    // Escape, X und Klick auf den Hintergrund gelten als Abbrechen.
    onModal2Close(() => decide(null));

    const mb = modalBody2();
    const cancelBtn = mb.querySelector("#vs-cancel") as HTMLButtonElement;
    cancelBtn.addEventListener("click", () => { decide(null); closeModal2(); });
    mb.querySelectorAll<HTMLButtonElement>("[data-choice]").forEach(btn => {
      btn.addEventListener("click", () => { decide(btn.dataset.choice as VacationSplitChoice); closeModal2(); });
    });
    cancelBtn.focus();
  });
}
